import React, { useEffect } from "react";
import { useUser } from "../../hooks/useUser";
import { useDispatch } from "react-redux";
import { fetchUserProfile } from "../../store";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "@reduxjs/toolkit";
import { EMPTY_USER_STATE } from "../../store/consts";
import posthog from "posthog-js";
import { extractDomainName } from "~/utils/extractDomainName";

// // // //

/**
 * UserLoader
 * Fetches current user via api (via Redux)
 * NOTE - this should only be implemented in the application *once* at the moment
 */
export function UserLoader(props: {
    children: (childProps: { loading: boolean }) => React.ReactNode;
}) {
    // Setup useUser hook to capture "loading" state
    const { loading, isLoggedIn, user } = useUser();

    // Dispatch the action to fetch the User state
    const dispatch: ThunkDispatch<{}, {}, AnyAction> = useDispatch();

    useEffect(() => {
        dispatch(fetchUserProfile());
    }, [dispatch]);

    // Invoke Posthog.identify if the user is logged in
    useEffect(() => {
        // Short-circuit if user isn't logged in
        // (Includes check against EMPTY_USER_STATE to be safe)
        if (!isLoggedIn || user.id === EMPTY_USER_STATE.id) {
            return;
        }

        // Invoke Posthog.identify
        // Doc: https://posthog.com/docs/product-analytics/identify
        const email_domain = extractDomainName(user.email);
        posthog.identify(
            user.email,
            {
                email: user.email,
                email_domain,
                // Include user.profile fields
                user_profile__company: user.profile.company,
                user_profile__company_type: user.profile.companyType,
                user_profile__acquisition_channel:
                    user.profile.acquisitionChannel,
                user_profile__asset_class_interests:
                    user.profile.assetClassInterests,
                user_profile__should_receive_marketing:
                    user.profile.shouldReceiveMarketing,
                user_profile__occupation: user.profile.occupation,
            },
            // `joined_at` is the user's signup date, which never changes - so we
            // send it via the `$set_once` argument to ensure a later identify can
            // never overwrite it. Omitted entirely when unavailable, rather than
            // writing a null over a value we already have in Posthog.
            user.createdAt ? { joined_at: user.createdAt } : undefined,
        );

        // Associate the user with their Organization for group analytics
        // Doc: https://posthog.com/docs/product-analytics/group-analytics
        // NOTE - guarded on the id being present because the API resolves a null
        // organization for users without one, and EMPTY_USER_STATE defaults it to
        // an empty string - either would register a junk group in Posthog.
        if (user.organization?.id) {
            posthog.group("organization", user.organization.id, {
                name: user.organization.name,
                domain: user.organization.domain,
            });
        }
    }, [isLoggedIn]);

    // Expose state via props.children
    return (
        <React.Fragment>
            {props.children({
                loading,
            })}
        </React.Fragment>
    );
}
